import { Controller } from "../../ts/controller";
import { Logger } from "../../ts/utils/logger";
import { ForumPost } from "../../ts/models/forum-post";
import { AppUrls } from "../../ts/app";

export class ForumPageController extends Controller {
    private _loadingMorePosts = false;
    private _currentPage = 1;
    private _totalPages: number | null = null;

    constructor() {
        super({
            logLabel: 'ForumPageController',
        });
    }

    public initialize(): void {
        this._setState();
        this._setupInfiniteScroll();
        Logger.info(this.logLabel, 'Initialized');
    }

    private _setState(): void {
        const spinner = this.getElementByIdOrWarn('forum-posts-loading-spinner');
        if (spinner) {
            spinner.style.visibility = this._loadingMorePosts ? 'visible' : 'hidden';
        }
    }

    private _insertPostsInPage(posts: ForumPost[]): void {
        Logger.info(this.logLabel, `Inserting ${posts.length} posts into the page`);
        const template = document.querySelector('.forum-post-template');
        if (!template) return;

        const fragment = document.createDocumentFragment();

        for (const post of posts) {
            const clone = template.cloneNode(true) as HTMLElement;
            clone.classList.remove('forum-post-template', 'hidden');

            const postUrl = `/forum/${post.id}`;
            clone.setAttribute('href', postUrl);
            clone.setAttribute('title', `Post - ${post.title}`);
            clone.setAttribute('aria-label', `Post - ${post.title}`);

            const titleEl = clone.querySelector('.title');
            const usernameEl = clone.querySelector('.username');
            const textPreviewEl = clone.querySelector('.text-preview');
            const createdAtEl = clone.querySelector('.created-at');
            const commentCountEl = clone.querySelector('.comment-count');
            const requestFlagEl = clone.querySelector('.request-flag');
            const fulfilledFlagEl = clone.querySelector('.request-fulfilled-flag');
            const mostRecentCommentPreviewEl = clone.querySelector('.most-recent-comment-preview');

            if (titleEl) titleEl.textContent = post.title;
            if (usernameEl) usernameEl.textContent = `@${post.user.username}`;
            if (textPreviewEl) textPreviewEl.textContent = post.text_preview;
            if (createdAtEl) {
                createdAtEl.textContent = post.created_at.toLocaleString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                });
            }
            if (commentCountEl) {
                if (post.comment_count > 0) {
                    commentCountEl.textContent = `${post.comment_count} ${post.comment_count === 1 ? 'comment' : 'comments'}`;
                    commentCountEl.classList.remove('hidden');
                } else {
                    commentCountEl.classList.add('hidden');
                }
            }
            if (requestFlagEl) {
                requestFlagEl.classList.toggle('hidden', !post.tab_request);
            }
            if (fulfilledFlagEl) {
                fulfilledFlagEl.classList.toggle('hidden', !post.fulfilled);
            }
            if (mostRecentCommentPreviewEl) {
                if (post.most_recent_comment_preview) {
                    mostRecentCommentPreviewEl.textContent = post.most_recent_comment_preview;
                    mostRecentCommentPreviewEl.classList.remove('hidden');
                }
            }


            fragment.appendChild(clone);
        }

        const postsContainer = document.getElementById('forum-posts');
        if (postsContainer) {
            postsContainer.appendChild(fragment);
        }
    }

    private _setupInfiniteScroll(): void {
        const sentinel = document.getElementById('forum-posts-infinite-scroll-sentinel');
        if (!sentinel) {
            Logger.error(this.logLabel, 'Infinite scroll sentinel not found in the DOM.');
            return;
        }

        const observer = new IntersectionObserver(
            async (entries) => {
                const target = entries[0];
                if (
                    target.isIntersecting &&
                    !this._loadingMorePosts
                    && (
                        this._totalPages === null
                        || this._currentPage < this._totalPages)) {
                    await this._getNextPageOfPosts();
                }
            },
            {
                root: null,
                rootMargin: '100px',
                threshold: 0.1,
            }
        );

        observer.observe(sentinel);
    }

    private async _getNextPageOfPosts(): Promise<void> {
        this._loadingMorePosts = true;
        this._setState();

        try {
            this._currentPage += 1;

            const startTime = Date.now();
            // Use the current URL to construct the endpoint
            const urlParams = new URLSearchParams(window.location.search);
            urlParams.set('page', this._currentPage.toString());
            const endpoint = `${AppUrls.getForumPosts}?${urlParams.toString()}`;
            const response = await fetch(endpoint, {
                method: 'GET',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Failed to fetch posts');
            }
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime < 1500) {
                await new Promise(resolve => setTimeout(resolve, 1000 - elapsedTime));
            }

            const data = await response.json();
            const posts = data.results.map(ForumPost.fromObject);
            this._totalPages = data.pages;
            this._insertPostsInPage(posts);
        } catch (error) {
            Logger.error(this.logLabel, 'Error fetching posts:', error);
        } finally {
            this._loadingMorePosts = false;
            this._setState();
        }
    }
}
